// useCoachMarks — Zustand der geführten Einmal-Hinweise im TTS-Flow. Leitet
// aus den Blöcken ab, welche Station dran ist (lib/coachMarks.ts), merkt
// überholte Stationen als gesehen und beobachtet, ob die Block-Spalte schon
// im Bild war (gleiche Regel wie der ScrollHint: ≥ 120 px sichtbar).

import { useEffect, useMemo, useState } from 'react';
import type { Block } from '@shared/types';
import {
  markCoachMarkSeen,
  passedCoachMarks,
  pickCoachMark,
  readSeenCoachMarks,
  type CoachMarkId,
} from '@/lib/coachMarks';
import { splitSentences } from '@/lib/splitSentences';
import { detectScript } from '@/lib/scriptDetect';

interface Args {
  blocks: Block[];
  /** Block-Spalte — sobald sie einmal im Bild war, starten die Hinweise. */
  blocksColEl: HTMLElement | null;
  allReady: boolean;
  /** Uploads dieser Session (Store-Zähler). */
  sessionUploads: number;
}

export function useCoachMarks({ blocks, blocksColEl, allReady, sessionUploads }: Args) {
  const [seen, setSeen] = useState<Set<CoachMarkId>>(() => readSeenCoachMarks());
  const [blocksSeen, setBlocksSeen] = useState(false);

  useEffect(() => {
    if (!blocksColEl || blocksSeen) return;
    const io = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setBlocksSeen(true); },
      { rootMargin: '0px 0px -120px 0px', threshold: 0 },
    );
    io.observe(blocksColEl);
    return () => io.disconnect();
  }, [blocksColEl, blocksSeen]);

  const hasTakes = blocks.some((b) => b.takes.length > 0);
  const hasFav = blocks.some((b) => b.favIdx >= 0 && !!b.takes[b.favIdx]);
  const dragged = blocks.some((b) => b.pinned);
  const canSplit = useMemo(() => {
    if (blocks.length !== 1) return false;
    const text = blocks[0].text;
    if (detectScript(text) === 'thai') return false;
    return splitSentences(text).length > 1;
  }, [blocks]);

  // Überholte Stationen gelten als gesehen — sonst käme „generate" nach dem
  // Upload-Reset der Takes beim nächsten Asset wieder.
  const uploaded = sessionUploads > 0;
  useEffect(() => {
    const passed = passedCoachMarks({ hasTakes, hasFav, allReady, dragged, uploaded });
    const fresh = passed.filter((id) => !seen.has(id));
    if (fresh.length === 0) return;
    fresh.forEach((id) => markCoachMarkSeen(id));
    setSeen((prev) => new Set([...prev, ...fresh]));
  }, [hasTakes, hasFav, allReady, dragged, uploaded, seen]);

  const active = pickCoachMark({ seen, blocksSeen, canSplit, hasTakes, hasFav, allReady });

  const dismiss = () => {
    if (!active) return;
    markCoachMarkSeen(active);
    setSeen((prev) => new Set([...prev, active]));
  };

  return { active, dismiss };
}
